/**
 * Triple Banner Carousel Component
 * Handles infinite carousel functionality with smooth transitions
 */

class TripleBannerCarousel {
  constructor(carouselId) {
    this.carouselId = carouselId;
    this.track = document.querySelector(`.c-triple-banner__carousel-track[data-carousel-id="${carouselId}"]`);
    this.slidesContainer = this.track?.querySelector('.c-triple-banner__carousel-slides');
    this.prevBtn = document.querySelector(`.c-triple-banner__carousel-btn--prev[data-carousel-id="${carouselId}"]`);
    this.nextBtn = document.querySelector(`.c-triple-banner__carousel-btn--next[data-carousel-id="${carouselId}"]`);
    this.indicators = document.querySelectorAll(`.c-triple-banner__indicator[data-carousel-id="${carouselId}"]`);
    
    if (!this.track || !this.slidesContainer) {
      console.warn('Triple Banner Carousel: Required elements not found');
      return;
    }

    this.slides = Array.from(this.slidesContainer.children);
    this.totalSlides = this.slides.length / 2; // Dividido por 2 porque duplicamos los slides
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.autoplayInterval = null;
    this.autoplayDelay = 5000; // 5 seconds

    this.init();
  }

  init() {
    if (this.totalSlides === 0) return;

    // Set initial position
    this.updatePosition(false);

    // Add event listeners
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Add event listeners to indicators
    this.indicators.forEach((indicator) => {
      indicator.addEventListener('click', () => {
        const slideIndex = parseInt(indicator.getAttribute('data-slide-index'), 10);
        this.goToSlide(slideIndex);
      });
    });

    // Handle transition end for infinite loop
    this.slidesContainer.addEventListener('transitionend', () => this.handleTransitionEnd());

    // Touch/swipe support
    this.addTouchSupport();

    // Keyboard navigation
    this.addKeyboardSupport();

    // Start autoplay
    this.startAutoplay();

    // Pause autoplay on hover
    this.track.addEventListener('mouseenter', () => this.stopAutoplay());
    this.track.addEventListener('mouseleave', () => this.startAutoplay());

    // Pause autoplay when tab is not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    });
  }

  updatePosition(animated = true) {
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const offset = -(this.currentIndex * slideWidth);
    
    if (!animated) {
      this.slidesContainer.style.transition = 'none';
    }
    
    this.slidesContainer.style.transform = `translateX(${offset}px)`;
    
    if (!animated) {
      // Force reflow
      this.slidesContainer.offsetHeight;
      this.slidesContainer.style.transition = '';
    }

    // Update indicators
    this.updateIndicators();
  }

  updateIndicators() {
    const activeIndex = this.currentIndex % this.totalSlides;
    this.indicators.forEach((indicator, index) => {
      if (index === activeIndex) {
        indicator.classList.add('c-triple-banner__indicator--active');
      } else {
        indicator.classList.remove('c-triple-banner__indicator--active');
      }
    });
  }

  goToSlide(index) {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    this.currentIndex = index;
    this.updatePosition(true);
    this.resetAutoplay();
  }

  nextSlide() {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    this.currentIndex++;
    this.updatePosition(true);
    this.resetAutoplay();
  }

  prevSlide() {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    this.currentIndex--;
    this.updatePosition(true);
    this.resetAutoplay();
  }

  handleTransitionEnd() {
    this.isTransitioning = false;

    // Handle infinite loop
    if (this.currentIndex >= this.totalSlides) {
      this.currentIndex = 0;
      this.updatePosition(false);
    } else if (this.currentIndex < 0) {
      this.currentIndex = this.totalSlides - 1;
      this.updatePosition(false);
    }
  }

  addTouchSupport() {
    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartTime = 0;

    this.track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartTime = Date.now();
    }, { passive: true });

    this.track.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const touchDuration = Date.now() - touchStartTime;
      const swipeDistance = Math.abs(touchEndX - touchStartX);
      
      // Only trigger swipe if it was quick and significant
      if (touchDuration < 500 && swipeDistance > 50) {
        if (touchEndX < touchStartX) {
          // Swipe left
          this.nextSlide();
        } else if (touchEndX > touchStartX) {
          // Swipe right
          this.prevSlide();
        }
      }
    }, { passive: true });
  }

  addKeyboardSupport() {
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.nextSlide();
      }
    });

    // Make track focusable for keyboard navigation
    if (!this.track.hasAttribute('tabindex')) {
      this.track.setAttribute('tabindex', '0');
    }
  }

  startAutoplay() {
    if (this.totalSlides <= 1) return; // No autoplay if only one slide
    
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  // Public method to handle window resize
  handleResize() {
    this.updatePosition(false);
  }
}

// Initialize all carousels on the page
function initTripleBannerCarousels() {
  const carouselTracks = document.querySelectorAll('.c-triple-banner__carousel-track[data-carousel-id]');
  const carousels = [];

  carouselTracks.forEach((track) => {
    const carouselId = track.getAttribute('data-carousel-id');
    if (carouselId) {
      const carousel = new TripleBannerCarousel(carouselId);
      carousels.push(carousel);
    }
  });

  // Handle window resize
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      carousels.forEach(carousel => {
        if (carousel.handleResize) {
          carousel.handleResize();
        }
      });
    }, 250);
  });

  return carousels;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTripleBannerCarousels);
} else {
  initTripleBannerCarousels();
}

// Re-initialize on Shopify section events (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.c-triple-banner')) {
      setTimeout(initTripleBannerCarousels, 100);
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(initTripleBannerCarousels, 100);
  });
}

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { TripleBannerCarousel, initTripleBannerCarousels };
}
